import { io, Socket } from "socket.io-client";
import { Dispatch } from "@reduxjs/toolkit";
import * as socketActions from "../redux/slices/OrderSocketState";
import { OrderFromDB } from "../redux/slices/types/OrderAndSocketState";
import { updateOrders } from "../redux/slices/OrderSocketState";
import { registerNewOrder } from "../redux/slices/OrderNotificationState";
import { getProdBaseURL } from "./xhr";
import { authStorage } from "../auth/authStorage";

export type SocketEvents = {
  connect: (dispatch: Dispatch) => void;
  disconnect: (dispatch: Dispatch) => void;
  connectionAck: (message: string) => void;
  simulate: (test: string) => void;
  newOrder: (order: OrderFromDB) => void;
};

type ManagedSocket = Socket<SocketEvents> & {
  __orderListenersAttached?: boolean;
  __orderConnectHandler?: () => void;
  __orderDisconnectHandler?: () => void;
  __orderNewOrderHandler?: (order: OrderFromDB) => void;
  __orderConnectErrorHandler?: (error: Error) => void;
};

export const initiateSocketIo = async (
  hasOrderFeature = false,
  dispatch: Dispatch<any>
): Promise<boolean | void> => {
  if (!hasOrderFeature) return false;
  const token = authStorage.getToken();
  let url = "http://localhost:3500";

  if (process.env.NODE_ENV === "production") {
    let prodURL = getProdBaseURL();
    url = prodURL.replace(/\/api\/$/, "");
  }

  if (url) {
    const socket = io(url, {
      query: {
        clientType: "cms",
      },
      withCredentials: true,
      auth: { token },
    }) as Socket<SocketEvents>;
    if (window && socket) {
      if (window.socket && window.socket !== socket) {
        cleanUpListeners(window.socket, dispatch);
        window.socket.disconnect();
      }

      window.socket = socket;
      handleSocketEvents(socket, dispatch);
      dispatch(socketActions.setSocketConnect({ isActive: socket.connected }));
    }
  }
};

const onConnect = (dispatch: Dispatch): void => {
  dispatch(
    socketActions.setSocketConnect({ isActive: !!window.socket?.connected })
  );
};

const onDisconnect = (dispatch: Dispatch) => {
  dispatch(socketActions.setSocketDisconnect({ isActive: false }));
};

const onNewOrder = (order: OrderFromDB, dispatch: Dispatch<any>) => {
  console.info("CMS newOrder handled", {
    id: order.id,
    orderNumber: order.orderNumber,
    tableSessionId: order.tableSessionId,
  });
  dispatch(updateOrders(order));
  dispatch(registerNewOrder(order));
};

export const handleSocketEvents = (
  socket: Socket<SocketEvents>,
  dispatch: Dispatch<any>
) => {
  if (!socket) return;

  const managedSocket = socket as ManagedSocket;
  if (managedSocket.__orderListenersAttached) return;

  managedSocket.__orderConnectHandler = () => onConnect(dispatch);
  managedSocket.__orderDisconnectHandler = () => onDisconnect(dispatch);
  managedSocket.__orderNewOrderHandler = (order) => onNewOrder(order, dispatch);
  managedSocket.__orderConnectErrorHandler = (error) => {
    console.warn("CMS socket connection failed", error.message);
    onDisconnect(dispatch);
  };

  socket.on("connect", managedSocket.__orderConnectHandler);
  socket.on("disconnect", managedSocket.__orderDisconnectHandler);
  socket.on("newOrder", managedSocket.__orderNewOrderHandler);
  socket.on(
    "connect_error" as keyof SocketEvents,
    managedSocket.__orderConnectErrorHandler as never
  );
  managedSocket.__orderListenersAttached = true;
};

export const cleanUpListeners = (
  socket: Socket<SocketEvents>,
  dispatch: Dispatch
) => {
  if (!socket) return;

  const managedSocket = socket as ManagedSocket;

  if (managedSocket.__orderConnectHandler) {
    socket.off("connect", managedSocket.__orderConnectHandler);
  }
  if (managedSocket.__orderDisconnectHandler) {
    socket.off("disconnect", managedSocket.__orderDisconnectHandler);
  }
  if (managedSocket.__orderNewOrderHandler) {
    socket.off("newOrder", managedSocket.__orderNewOrderHandler);
  }
  if (managedSocket.__orderConnectErrorHandler) {
    socket.off(
      "connect_error" as keyof SocketEvents,
      managedSocket.__orderConnectErrorHandler as never
    );
  }

  managedSocket.__orderListenersAttached = false;
};
